import React from 'react';
import { Film, Video, Upload, Sparkles, RefreshCw, Zap } from 'lucide-react';

interface HighlightEmptyIllustrationProps {
  title?: string;
  description?: string;
  selectedSport?: string;
  onResetSportFilter?: () => void;
  onUploadHighlight?: () => void;
}

export const HighlightEmptyIllustration: React.FC<HighlightEmptyIllustrationProps> = ({
  title = "No Highlight Reels Found",
  description = "No 30-second game clips, clutch buzzer beaters, or player highlights found in this category. Be the first athlete to upload your reel!",
  selectedSport = "all",
  onResetSportFilter,
  onUploadHighlight,
}) => {
  return (
    <div className="relative w-full p-8 md:p-12 text-center bg-gradient-to-b from-indigo-950/90 via-indigo-900/60 to-indigo-950/90 rounded-[2.5rem] border border-white/15 shadow-2xl overflow-hidden space-y-6 group">
      {/* Ambient Backdrop Glow & Grid Lines */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-sky-400/10 via-transparent to-transparent pointer-events-none" />
      <div className="absolute -top-24 -right-24 w-60 h-60 bg-sky-400/15 rounded-full blur-3xl pointer-events-none animate-pulse" />
      <div className="absolute -bottom-24 -left-24 w-60 h-60 bg-lime-400/15 rounded-full blur-3xl pointer-events-none animate-pulse" />

      {/* CUSTOM SVG VECTOR ILLUSTRATION FOR HIGHLIGHT REEL */}
      <div className="relative w-48 h-48 mx-auto flex items-center justify-center">
        {/* Outer Glowing Energy Rings */}
        <div className="absolute inset-0 rounded-full border-2 border-dashed border-sky-400/30 animate-[spin_25s_linear_infinite]" />
        <div className="absolute inset-3 rounded-full border border-white/10 bg-indigo-950/80 backdrop-blur-md shadow-2xl flex items-center justify-center" />

        {/* Video Film Clapperboard & Aperture Vector Artwork */}
        <svg
          viewBox="0 0 200 200"
          className="w-full h-full relative z-10 drop-shadow-[0_10px_25px_rgba(56,189,248,0.3)]"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Film Strip Frame Background */}
          <rect x="30" y="55" width="140" height="95" rx="16" fill="#090d26" stroke="#ffffff" strokeOpacity="0.15" strokeWidth="2" />

          {/* Film Strip Perforations (Top & Bottom) */}
          <g fill="#38bdf8" opacity="0.6">
            <rect x="40" y="60" width="8" height="6" rx="1" />
            <rect x="60" y="60" width="8" height="6" rx="1" />
            <rect x="80" y="60" width="8" height="6" rx="1" />
            <rect x="100" y="60" width="8" height="6" rx="1" />
            <rect x="120" y="60" width="8" height="6" rx="1" />
            <rect x="140" y="60" width="8" height="6" rx="1" />

            <rect x="40" y="139" width="8" height="6" rx="1" />
            <rect x="60" y="139" width="8" height="6" rx="1" />
            <rect x="80" y="139" width="8" height="6" rx="1" />
            <rect x="100" y="139" width="8" height="6" rx="1" />
            <rect x="120" y="139" width="8" height="6" rx="1" />
            <rect x="140" y="139" width="8" height="6" rx="1" />
          </g>

          {/* Cinema Clapperboard Top Bar */}
          <g transform="translate(30, 32)">
            {/* Clapper Base Board */}
            <path d="M 0 16 L 140 0 L 140 18 L 0 32 Z" fill="url(#clapperGrad)" stroke="#a3e635" strokeWidth="2" />
            {/* White Stripes */}
            <path d="M 20 14 L 35 12 L 20 28 L 5 30 Z" fill="#ffffff" />
            <path d="M 50 10 L 65 8 L 50 24 L 35 26 Z" fill="#ffffff" />
            <path d="M 80 6 L 95 4 L 80 20 L 65 22 Z" fill="#ffffff" />
            <path d="M 110 2 L 125 0 L 110 16 L 95 18 Z" fill="#ffffff" />
          </g>

          {/* Play Button Aura Ring (Center) */}
          <circle cx="100" cy="102" r="22" fill="#a3e635" fillOpacity="0.15" stroke="#a3e635" strokeWidth="2.5" />
          <path d="M 94 91 L 114 102 L 94 113 Z" fill="#a3e635" />

          {/* Floating Sparkles & Lightning Accents */}
          <path d="M 35 30 L 37 35 L 42 37 L 37 39 L 35 44 L 33 39 L 28 37 L 33 35 Z" fill="#a3e635" />
          <path d="M 165 30 L 166.5 34 L 170.5 35.5 L 166.5 37 L 165 41 L 163.5 37 L 159.5 35.5 L 163.5 34 Z" fill="#38bdf8" />
          <path d="M 168 120 L 163 128 H 168 L 165 138 L 175 127 H 170 Z" fill="#fbbf24" />

          {/* Gradient Definition */}
          <defs>
            <linearGradient id="clapperGrad" x1="0" y1="0" x2="140" y2="32" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#0f172a" />
              <stop offset="50%" stopColor="#1e293b" />
              <stop offset="100%" stopColor="#0f172a" />
            </linearGradient>
          </defs>
        </svg>

        {/* Floating XP Reward Badge Indicator */}
        <div className="absolute -top-1 -right-1 bg-gradient-to-r from-sky-400 to-lime-400 text-black font-black text-[10px] px-2.5 py-1 rounded-xl shadow-lg flex items-center space-x-1 border border-white/30 animate-bounce">
          <Zap className="w-3 h-3 fill-black" />
          <span>+50 XP Bonus</span>
        </div>
      </div>

      {/* Copy & Status Message */}
      <div className="space-y-2 max-w-md mx-auto relative z-10">
        <div className="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-sky-400/10 border border-sky-400/30 text-sky-300 text-[10px] font-black uppercase tracking-wider font-mono">
          <Film className="w-3 h-3 text-sky-400" />
          <span>Category Filter: {selectedSport.toUpperCase()}</span>
        </div>

        <h3 className="text-lg md:text-xl font-black italic uppercase tracking-wide text-white drop-shadow-md">
          {title}
        </h3>
        <p className="text-xs md:text-sm text-indigo-200/80 font-medium leading-relaxed">
          {description}
        </p>
      </div>

      {/* Interactive Action Buttons */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2 max-w-sm mx-auto relative z-10">
        {onResetSportFilter && (
          <button
            type="button"
            onClick={onResetSportFilter}
            className="w-full sm:w-auto px-5 py-2.5 bg-indigo-900/90 hover:bg-indigo-800 text-sky-300 border border-sky-400/40 text-xs font-black italic uppercase rounded-2xl transition shadow-lg flex items-center justify-center space-x-2 active:scale-95"
          >
            <RefreshCw className="w-4 h-4 text-sky-400" />
            <span>Show All Sports</span>
          </button>
        )}

        {onUploadHighlight && (
          <button
            type="button"
            onClick={onUploadHighlight}
            className="w-full sm:w-auto px-5 py-2.5 bg-gradient-to-r from-lime-400 via-emerald-400 to-lime-400 text-black font-black italic uppercase text-xs rounded-2xl shadow-xl shadow-lime-400/25 hover:scale-105 transition flex items-center justify-center space-x-2 active:scale-95"
          >
            <Upload className="w-4 h-4 stroke-[3]" />
            <span>Upload Highlight Clip (+50 XP)</span>
          </button>
        )}
      </div>
    </div>
  );
};
